'use client';
import type { ReactNode } from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import useMemoObject from './useMemoObject';

// InteractionContext is in charge of the state management across components. This way the select
// and the hover state can be kept in sync without a local state in the outermost chart component.

type InteractionContextValue = {
  handleHoverTick: (value: number) => void;
  handleMouseEnter: (id: string, event: MouseEvent) => void;
  handleMouseLeave: (id: string, event: MouseEvent) => void;
  handleClick: (id: string, event: MouseEvent) => void;
};

const defaultInteractionContext: InteractionContextValue = {
  handleHoverTick: () => {},
  handleMouseEnter: () => {},
  handleMouseLeave: () => {},
  handleClick: () => {},
};

const InteractionContext = createContext<InteractionContextValue>(defaultInteractionContext);

const SelectedContext = createContext<(id: string) => boolean>(() => false);
const HoveredContext = createContext<(id: string) => boolean>(() => false);
const HoveredTickContext = createContext<number | undefined>(undefined);
/**
 * Not to be used in render. Safe to use in click handlers (does not trigger update on every tick update)
 */
const GetHoveredTickContext = createContext<() => number | undefined>(() => undefined);

const toggleSelected = (id: string) => (state: string[]) =>
  state.includes(id) ? state.filter(s => s !== id) : [...state, id];

const selectOne = (id: string) => (state: string[]) =>
  state.length === 1 && state[0] === id ? [] : [id];

interface InteractionProviderProps {
  selectShift?: boolean;
  selectToggle?: boolean;
  children: ReactNode;
}

export const InteractionContextProvider = memo(
  ({ selectShift, selectToggle, children }: InteractionProviderProps) => {
    const [hoveredTick, setHoveredTick] = useState<number | undefined>(undefined);
    const [hovered, setHovered] = useState<string | undefined>(undefined);
    const [selected, setSelected] = useState<string[]>([]);

    const isSelected = useCallback((id: string) => selected.includes(id), [selected]);
    const isHovered = useCallback((id: string) => id === hovered, [hovered]);

    const handleHoverTick = useCallback((value: number) => {
      setHoveredTick(prev => (prev === value ? prev : value));
    }, []);

    const hoveredTickRef = useRef(hoveredTick);
    useEffect(() => {
      hoveredTickRef.current = hoveredTick;
    });
    const getHoveredTick = useCallback(() => {
      return hoveredTickRef.current;
    }, []);

    const handleMouseEnter = useCallback((id: string, event: MouseEvent) => {
      event.stopPropagation();
      setHovered(prevId => (prevId !== id ? id : prevId));
    }, []);

    const handleMouseLeave = useCallback((id: string, _event?: MouseEvent) => {
      setHovered(prevId => (prevId === id ? undefined : prevId));
    }, []);

    const handleClick = useCallback(
      (id: string, event: MouseEvent) => {
        event.stopPropagation();
        const shiftKey = Boolean(selectShift && event && 'shiftKey' in event && event.shiftKey);
        const updateSelected = selectToggle || shiftKey ? toggleSelected : selectOne;
        setSelected(id ? updateSelected(id) : []);
      },
      [selectToggle, selectShift],
    );

    const value = useMemoObject<InteractionContextValue>({
      handleHoverTick,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
    });

    return (
      <InteractionContext.Provider value={value}>
        <GetHoveredTickContext.Provider value={getHoveredTick}>
          <SelectedContext.Provider value={isSelected}>
            <HoveredContext.Provider value={isHovered}>
              <HoveredTickContext.Provider value={hoveredTick}>
                {children}
              </HoveredTickContext.Provider>
            </HoveredContext.Provider>
          </SelectedContext.Provider>
        </GetHoveredTickContext.Provider>
      </InteractionContext.Provider>
    );
  },
);
export const useInteractionContext = () => useContext(InteractionContext);
export const useSelectedContext = () => useContext(SelectedContext);
export const useHoveredContext = () => useContext(HoveredContext);
export const useGetHoveredTickContext = () => useContext(GetHoveredTickContext);
export const useHoveredTickContext = () => useContext(HoveredTickContext);
